import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, FormControlLabel, Switch, Checkbox, MenuItem, Stack, InputAdornment, IconButton, Typography, Alert, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createEvent, createEventWithGallery } from '../../controllers/clubEventsController';
import EntityGalleryFormSection from '../../components/EntityGalleryFormSection';
import { buildCreateEntityGalleryDto, initialEntityGalleryForm } from '../../constants/galleryEntityTypes';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { datetimeLocalToApiString } from '../../utils/turkeyLocalDateTime';

const DEFAULT_EVENT_INCOME_ARP_CODE = '603.003.116';

const normalizeLogoCariCode = (value) => {
  const trimmed = String(value || '').trim();
  return trimmed || null;
};

const MapPicker = ({ value, onChange, open }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Ensure container has dimensions
    const ensure = () => {
      if (!ref.current || ref.current.clientWidth === 0 || ref.current.clientHeight === 0) {
        setTimeout(ensure, 50);
        return false;
      }
      return true;
    };
    if (!ensure()) return;

    // Fix marker icons in bundlers
    L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

    const center = [value?.lat || 41.015137, value?.lng || 28.97953];
    const map = L.map(ref.current, { center, zoom: 6 });
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors' }).addTo(map);

    if (value?.lat && value?.lng) {
      markerRef.current = L.marker(center).addTo(map);
    }
    map.on('click', (e) => {
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      if (markerRef.current) markerRef.current.remove();
      markerRef.current = L.marker([lat, lng]).addTo(map);
      onChange?.({ lat, lng });
    });

    // Invalidate size after mount (guarded)
    timeoutRef.current = setTimeout(() => {
      try {
        if (map && map._container && map._mapPane) {
          map.invalidateSize();
        }
      } catch (e) {}
    }, 100);

    return () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      try { map.remove(); } catch (e) {}
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [open]);

  return <Box ref={ref} sx={{ height: 320, minHeight: 320, width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default' }} />;
};

const initialForm = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  city: '',
  address: '',
  venueName: '',
  country: 'Türkiye',
  latitude: '',
  longitude: '',
  price: '',
  capacity: 500,
  eventType: 0,
  isActive: true,
  onlineMeetingLink: '',
  allowGuests: false,
  guestCapacity: 0,
  logoCariCode: '',
  sendPushNotification: false,
};

const CreateClubEventDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [galleryForm, setGalleryForm] = useState(initialEntityGalleryForm);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setError('');
      setGalleryForm(initialEntityGalleryForm);
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleCapacityChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setForm((f) => ({ ...f, capacity: '' }));
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed < 1) return;
    setForm((f) => ({ ...f, capacity: parsed }));
  };

  const handleGuestCapacityChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setForm((f) => ({ ...f, guestCapacity: '' }));
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed < 1) return;
    setForm((f) => ({ ...f, guestCapacity: parsed }));
  };

  const handleSubmit = async () => {
    setError('');
    const capacity = Number(form.capacity);
    if (!capacity || capacity <= 0) {
      setError('Kapasite 0\'dan büyük olmalıdır');
      return;
    }
    const guestCapacity = Number(form.guestCapacity);
    if (form.allowGuests && (!guestCapacity || guestCapacity <= 0)) {
      setError('Misafir kapasitesi 0\'dan büyük olmalıdır');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        startDate: datetimeLocalToApiString(form.startDate),
        endDate: datetimeLocalToApiString(form.endDate),
        location: (form.city || form.address || form.venueName || form.latitude || form.longitude)
          ? {
              city: form.city || '',
              address: form.address || null,
              venueName: form.venueName || '',
              country: form.country || 'Türkiye',
              latitude: form.latitude === '' ? 0 : Number(form.latitude),
              longitude: form.longitude === '' ? 0 : Number(form.longitude),
            }
          : null,
        price: form.price === '' ? null : Number(form.price),
        capacity,
        eventType: Number(form.eventType) || 0,
        isActive: Boolean(form.isActive),
        onlineMeetingLink: form.onlineMeetingLink?.trim() || null,
        allowGuests: Boolean(form.allowGuests),
        guestCapacity: form.allowGuests ? guestCapacity : 0,
        logoCariCode: normalizeLogoCariCode(form.logoCariCode),
        sendPushNotification: Boolean(form.sendPushNotification),
      };
      const galleryDto = buildCreateEntityGalleryDto(galleryForm);
      if (galleryDto) payload.gallery = galleryDto;

      const hasGalleryImages = galleryForm.enabled && (galleryForm.files?.length || 0) > 0;
      if (hasGalleryImages) {
        await createEventWithGallery({ dto: payload, galleryImages: galleryForm.files });
      } else {
        await createEvent(payload);
      }

      if (onCreated) {
        await onCreated({
          sendPushNotification: Boolean(form.sendPushNotification),
        });
      }
    } catch (e) {
      setError(e?.message || 'Etkinlik oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yeni Etkinlik
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <EntityGalleryFormSection
              value={galleryForm}
              onChange={setGalleryForm}
              disabled={submitting}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Başlık
              </Typography>
              <TextField
                fullWidth
                required
                value={form.title}
                onChange={handleChange('title')}
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Açıklama
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={form.description}
                onChange={handleChange('description')}
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Başlangıç
                </Typography>
                <TextField
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.startDate}
                  onChange={handleChange('startDate')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Bitiş
                </Typography>
                <TextField
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.endDate}
                  onChange={handleChange('endDate')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Şehir
                </Typography>
                <TextField
                  fullWidth
                  value={form.city}
                  onChange={handleChange('city')}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Adres
                </Typography>
                <TextField
                  fullWidth
                  value={form.address}
                  onChange={handleChange('address')}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Mekan Adı
                </Typography>
                <TextField
                  fullWidth
                  value={form.venueName}
                  onChange={handleChange('venueName')}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Ülke
                </Typography>
                <TextField
                  fullWidth
                  value={form.country}
                  onChange={handleChange('country')}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
          </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Konum Seçimi
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: '#e6e6e6', borderRadius: '5px', overflow: 'hidden' }}>
            <MapPicker
              value={{ lat: form.latitude ? Number(form.latitude) : undefined, lng: form.longitude ? Number(form.longitude) : undefined }}
              onChange={({ lat, lng }) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
              open={open}
            />
          </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Enlem (Latitude)
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  value={form.latitude}
                  disabled
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Boylam (Longitude)
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  value={form.longitude}
                  disabled
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Ücret
                </Typography>
                <TextField
                  type="number"
                  inputProps={{ step: '0.01' }}
                  fullWidth
                  value={form.price}
                  onChange={handleChange('price')}
                  InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Logo gelir cari kodu (opsiyonel)
              </Typography>
              <TextField
                fullWidth
                value={form.logoCariCode}
                onChange={handleChange('logoCariCode')}
                placeholder={`Boş bırakılırsa ${DEFAULT_EVENT_INCOME_ARP_CODE}`}
                helperText="Etkinlik borç fişinde alacak tarafı. Üye cari kodu değildir."
                inputProps={{ maxLength: 60 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '12px',
                    color: '#a3a3a3',
                    mx: 0,
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Kapasite
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  value={form.capacity}
                  onChange={handleCapacityChange}
                  placeholder=""
                  required
                  inputProps={{ min: 1, step: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
          </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Tür
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={form.eventType}
                  onChange={handleChange('eventType')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                >
              <MenuItem value={0}>Gezi</MenuItem>
              <MenuItem value={1}>Toplantı</MenuItem>
              <MenuItem value={2}>Sergi</MenuItem>
              <MenuItem value={3}>Konser</MenuItem>
              <MenuItem value={4}>Atölye</MenuItem>
              <MenuItem value={5}>Seminer</MenuItem>
              <MenuItem value={6}>Balo</MenuItem>
              <MenuItem value={7}>Yemek</MenuItem>
              <MenuItem value={8}>Ladies&apos; Night</MenuItem>
            </TextField>
          </Box>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Online Toplantı Linki
              </Typography>
          <TextField 
            fullWidth 
            type="url"
            value={form.onlineMeetingLink} 
            onChange={handleChange('onlineMeetingLink')}
            placeholder="https://..."
            inputProps={{ maxLength: 500 }}
            helperText="Zoom, Teams, Google Meet vb. online toplantı linki (opsiyonel)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
          />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Durum
                </Typography>
            <FormControlLabel 
              control={
                    <Switch
                  checked={form.allowGuests} 
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    allowGuests: e.target.checked,
                    guestCapacity: e.target.checked
                      ? (Number(f.guestCapacity) > 0 ? f.guestCapacity : 3)
                      : 0,
                  }))} 
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0E4D94',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0E4D94',
                        },
                      }}
                />
              } 
                  label={form.allowGuests ? 'Misafir Getirilebilir' : 'Misafir Getirilemez'}
                  sx={{
                    ml: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      ml: 1,
                    },
                  }}
            />
              </Box>
            {form.allowGuests && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Misafir Kapasitesi (Üye Başına)
                  </Typography>
              <TextField 
                type="number" 
                fullWidth 
                value={form.guestCapacity} 
                onChange={handleGuestCapacityChange}
                required
                inputProps={{ min: 1, step: 1 }}
                helperText="Her üye kaç misafir getirebilir?"
                    placeholder=""
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
              />
                </Box>
            )}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Durum
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#0E4D94',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#0E4D94',
                      },
                    }}
                  />
                }
                label={form.isActive ? 'Aktif' : 'Pasif'}
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '13px',
                    ml: 1,
                  },
                }}
              />
          </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.sendPushNotification}
                    onChange={(e) => setForm((f) => ({ ...f, sendPushNotification: e.target.checked }))}
                    sx={{
                      color: '#a3a3a3',
                      '&.Mui-checked': {
                        color: '#0E4D94',
                      },
                    }}
                  />
                }
                label="Uygulama bildirimi gönderilsin mi?"
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '13px',
                  },
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#a3a3a3', ml: 4, mt: -0.5 }}>
                İşaretlenirse etkinlik başlığı ve açıklaması tüm üyelere uygulama bildirimi olarak gönderilir.
              </Typography>
            </Box>
        </Stack>
      </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Oluştur'
            )}
          </Button>
      </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateClubEventDialog;


